import ShopType from "@/enum/ShopType";
import GlobalStore from "@/store";
import { observable, when, makeObservable, actionBound, computed } from "mobx";
import { getEncrytionToken, getOnceToken } from "@/services/JdSecurity";
import { loadRemoteScript } from "@/utils/scriptLoader";
import { getClientIp } from "@/services/api/user";
import StorageManager from "@/utils/StorageManager";
import delay from "@/utils/delay";

interface NpsCustomParams {
    nps: {
        npsNodeIdLv1: string; // 传入一级场景id
        npsNodeIdLv2: string; // 传入自己应用的二级场景id
        token: string; // 传入token证明是isv商家
        npsStatus: boolean;
        placement: string; //弹出位置
    };
}

export const JD_MESSAGE_BOX = "jd-message-box";

class JdSDKStore {
    appKey = "F7C226A97CA6AA49165D8636D910D63B";

    messageBoxInstance: JdMicroAppOpenInstance;

    reachPopupInstance: JdMicroAppOpenInstance;

    get showMessageBox() {
        const shop = this.globalStore.shopMapping.get(this.globalStore.currentUser?.shopId);

        return [ShopType.JD_PURCHASE, ShopType.JD].includes(shop?.type) && !/JM_PC/i.test(navigator.userAgent);
    }

    get showReachPopup() {
        const shop = this.globalStore.shopMapping.get(this.globalStore.currentUser?.shopId);

        if (![ShopType.JD_PURCHASE, ShopType.JD].includes(shop?.type)) return false;

        if (
            this.globalStore.shops.every(({ type }) =>
                [ShopType.JD_PURCHASE, ShopType.JD, ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL].includes(type),
            )
        ) {
            return false;
        }

        return true;
    }

    encryptedToken: string;

    npsCustomParams: NpsCustomParams;

    ipLoading = false;

    ip = null;

    constructor(public globalStore: typeof GlobalStore) {
        makeObservable(this, {
            showMessageBox: computed,
            showReachPopup: computed,
            initMessageBox: actionBound,
            initReachPopup: actionBound,
            encryptedToken: observable,
            npsCustomParams: observable,
            ipLoading: observable,
            ip: observable,
            getIp: actionBound,
            init: actionBound,
            loadToken: actionBound,
            initJdNPS: actionBound,
        });
        this.init();

        this.getIp();
    }

    async getIp() {
        await when(() => this.globalStore.isInit);

        const hasJdShop = this.globalStore.shops.some(({ type }) =>
            [ShopType.JD, ShopType.JD_PURCHASE, ShopType.JD_DROP_SHIPPING_SUPPLIER].includes(type),
        );

        if (!hasJdShop) return;

        try {
            this.ipLoading = true;

            const ipCache = StorageManager.getIPCache();

            if (!ipCache) {
                /** 拦截器中使用该属性，为避免阻塞用户正常使用，如果3s内无获取到IP，正常放行 */
                setTimeout(() => {
                    if (this.ipLoading) {
                        this.ipLoading = false;
                    }
                }, 3 * 1000);

                const clientIp = await getClientIp();

                this.ip = clientIp;

                StorageManager.setIPCache(clientIp);
            } else {
                this.ip = ipCache;
            }
        } catch (e) {
            console.log("error:", e);
        } finally {
            this.ipLoading = false;
        }
    }

    async init() {
        await when(() => this.globalStore.isInit);

        if (!this.globalStore.currentUser) return;

        this.initMessageBox();

        this.initReachPopup();

        const currentUserShopId = this.globalStore.currentUser.shopId;
        const currentUserShop = this.globalStore.shopMapping.get(currentUserShopId);

        if (currentUserShop.type !== ShopType.JD) return;

        await this.loadToken(currentUserShopId);

        await this.initJdNPS();
    }

    async loadToken(shopId: number) {
        const { encryptedToken } = await getEncrytionToken(shopId, { hideModal: true });

        this.encryptedToken = encryptedToken;
    }

    /** 登录初始化时，将消息盒子挂载到导航栏京麦消息元素。
     * @see https://open.jd.com/v2/#/doc/dev-guide?listId=1101822
     */
    async initMessageBox() {
        if (!this.showMessageBox) return;

        try {
            if (!this.messageBoxInstance) {
                await loadRemoteScript("https://microapp-open.jd.com/micro-app-open/index.js");

                if (!this.showMessageBox) return;

                this.messageBoxInstance = window.jdMicroAppOpen.create();
            }

            await delay(30);

            const target = document.getElementById(JD_MESSAGE_BOX);
            if (!target) return;

            this.messageBoxInstance.clear();

            const { onceToken } = await getOnceToken(
                this.globalStore.currentUser.shopId,
                "23F90A0DC7C77FDED55B417A588A3CA9",
            );

            if (!onceToken) return;

            this.messageBoxInstance.open({
                target,
                appKey: this.appKey,
                componentId: "23F90A0DC7C77FDED55B417A588A3CA9",
                getOnceToken: async () => {
                    return onceToken;
                },
                customParams: {
                    // 与 topNavLayout.less 中顶部菜单的配色保持一致。
                    backGroundColor: "#048fc2",
                    iconColor: "#fff",
                    textColor: "#fff",
                    hoverBackGroundColor: "#0377a2",
                    hoverIconColor: "#fff",
                    hoverTextColor: "#fff",
                    borderRadius: "unset",
                },
            });
        } catch {}
    }

    /** 登录初始化时打开商家触达弹窗。
     * @see https://open.jd.com/v2/#/doc/dev-guide?listId=1101822
     */
    async initReachPopup() {
        if (!this.showReachPopup) return;

        try {
            if (!this.reachPopupInstance) {
                await loadRemoteScript("https://microapp-open.jd.com/micro-app-open/index.js");

                if (!this.showReachPopup) return;

                this.reachPopupInstance = window.jdMicroAppOpen.create();
            }

            this.reachPopupInstance.clear();

            const { onceToken } = await getOnceToken(
                this.globalStore.currentUser.shopId,
                "58B53BF0732974A27FC2867032CFDC7A",
            );

            if (!onceToken) return;

            this.reachPopupInstance.open({
                appKey: this.appKey,
                componentId: "58B53BF0732974A27FC2867032CFDC7A",
                getOnceToken: async () => {
                    return onceToken;
                },
            });
        } catch {}
    }

    /**
     * @see https://www.tapd.cn/22185411/prong/tasks/view/1122185411001004512?url_cache_key=from_url_task_list_0b7a4f69b2951d17f49d528a107c98f2&action_entry_type=task_list
     */
    async initJdNPS() {
        await loadRemoteScript(
            "//jm-static.jd.com/shop-common-components/shop-common-components.iife.js?appId=CSK3O55VX27DSBRPNLM0",
        );

        window.shopCommonComponents.registerCommonComponents(); // 注册公共组件

        this.npsCustomParams = {
            nps: {
                npsNodeIdLv1: "CJ_LISTEN_JM_ISV",
                npsNodeIdLv2: "CJ_LISTEN_JM_ISV_FHY",
                token: this.encryptedToken,
                npsStatus: true,
                /** 右下角弹出 */
                placement: "right",
            },
        };
    }
}

export default JdSDKStore;
